import { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { StaffCombobox } from "@/components/StaffCombobox";
import { TreePalm, Trash2, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import { BackupPicker } from "@/components/tasks/BackupPicker";

export const fmtDue = (iso) => { try { return new Date(iso + "T00:00:00").toLocaleDateString(undefined, { month: "short", day: "numeric" }); } catch { return iso; } };
export const fmtRange = (a, b) => `${fmtDue(a)} – ${fmtDue(b)}`;
const BLANK = { start: "", end: "", note: "", user_email: "", user_name: "", backup_email: "", backup_name: "" };
const plural = (n, w) => `${n} ${w}${n === 1 ? "" : "s"}`;

function OooRow({ o, showPerson, onRemove, onChanged }) {
  return (
    <li className="flex items-center gap-2 text-sm border border-border rounded-sm px-2.5 py-1.5" data-testid="ooo-row">
      <span className={`text-[10px] uppercase tracking-wide font-semibold px-1.5 py-0.5 rounded-sm shrink-0 ${o.status === "away" ? "bg-amber-100 text-amber-700" : "bg-secondary text-muted-foreground"}`}>{o.status === "away" ? "Away" : "Upcoming"}</span>
      <span className="flex-1 min-w-0 truncate">
        {showPerson && <span className="font-medium">{o.user_name} · </span>}{fmtRange(o.start, o.end)}
        <span className="text-muted-foreground"> · back {fmtDue(o.back)}</span>
        {o.backup_name && <span className="text-muted-foreground inline-flex items-center gap-1 ml-1" data-testid="ooo-row-backup"><ArrowRight className="h-3 w-3" />{o.backup_name}</span>}
        {o.note && <span className="text-muted-foreground"> — {o.note}</span>}
      </span>
      {!o.backup_email && <BackupPicker entry={o} onSaved={onChanged} />}
      <button onClick={() => onRemove(o)} className="text-muted-foreground hover:text-destructive shrink-0" title="Remove" data-testid={`ooo-delete-${o.id}`}><Trash2 className="h-3.5 w-3.5" /></button>
    </li>
  );
}

export function OutOfOfficeDialog({ open, setOpen, onChanged }) {
  const { user } = useAuth();
  const isMgr = ["admin", "manager"].includes(user?.role);
  const [form, setForm] = useState(BLANK);
  const [rows, setRows] = useState([]);
  const load = useCallback(() => api.get("/ooo", { params: { scope: isMgr ? "all" : "mine" } }).then((r) => setRows(r.data)).catch((e) => console.error("ooo", e)), [isMgr]);
  useEffect(() => { if (open) { load(); setForm(BLANK); } }, [open, load]);

  const save = async () => {
    if (!form.start || !form.end) return toast.error("Pick your first and last day away");
    try {
      const { data } = await api.post("/ooo", form);
      toast.success(data.moved ? `Out of office saved — ${plural(data.moved, "recurring task")} moved to ${fmtDue(data.back)}` : "Out of office saved");
      setForm(BLANK); load(); onChanged?.();
    } catch (e) { console.error("ooo save", e); toast.error(e?.response?.data?.detail || "Could not save"); }
  };
  const remove = async (o) => {
    try {
      const { data } = await api.delete(`/ooo/${o.id}`);
      toast.success(data.restored ? `Removed — ${plural(data.restored, "task")} back on the original date` : "Removed");
      load(); onChanged?.();
    } catch (e) { console.error("ooo delete", e); toast.error(e?.response?.data?.detail || "Could not remove"); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="max-w-lg" data-testid="ooo-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><TreePalm className="h-5 w-5 text-primary" /> Out of office</DialogTitle>
          <DialogDescription>Recurring tasks due while away move to the first working day back — the series keeps its normal schedule.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          {isMgr && <div><Label className="text-xs">Person</Label><StaffCombobox testid="ooo-person" value={form.user_name} placeholder="Me (default)" onChange={(name, email) => setForm({ ...form, user_name: name, user_email: email })} /></div>}
          <div className="grid grid-cols-2 gap-3">
            <div><Label className="text-xs">First day away</Label><Input type="date" data-testid="ooo-start" value={form.start} onChange={(e) => setForm({ ...form, start: e.target.value })} /></div>
            <div><Label className="text-xs">Last day away</Label><Input type="date" data-testid="ooo-end" min={form.start} value={form.end} onChange={(e) => setForm({ ...form, end: e.target.value })} /></div>
          </div>
          <div><Label className="text-xs">Note (optional)</Label><Input data-testid="ooo-note" value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} placeholder="e.g. Vacation — Brandy is covering" /></div>
          <div>
            <Label className="text-xs">Backup (optional)</Label>
            <StaffCombobox testid="ooo-backup" value={form.backup_name} placeholder="Who covers for you?" onChange={(name, email) => setForm({ ...form, backup_name: name, backup_email: email })} />
            <span className="block text-[11px] text-muted-foreground mt-1">Anyone assigning a ticket or task to this person while they're away gets a one-click “send to backup instead”.</span>
          </div>
          <Button className="w-full" onClick={save} data-testid="ooo-save"><TreePalm className="h-4 w-4 mr-1.5" /> Save out of office</Button>
        </div>
        <div className="border-t border-border pt-3 mt-1">
          <div className="text-xs font-semibold mb-2">{isMgr ? "Who’s away or scheduled out" : "Your upcoming time away"}</div>
          {rows.length === 0
            ? <p className="text-xs text-muted-foreground" data-testid="ooo-empty">Nothing scheduled.</p>
            : <ul className="space-y-1.5 max-h-48 overflow-y-auto">{rows.map((o) => <OooRow key={o.id} o={o} showPerson={isMgr} onRemove={remove} onChanged={() => { load(); onChanged?.(); }} />)}</ul>}
        </div>
      </DialogContent>
    </Dialog>
  );
}
